import { useRef, useEffect, useState, useMemo } from "react";
import type { Order } from "~/features/orders/types-order";
import { CALL_FLOW_OPTIONS, CALL_FLOW, isDev, type CallFlowDisplayName } from "~/features/orders/constants";
import { RxStatus } from "~/features/orders/types-order";
import { getOrderCity, getOrderDisplayPhone, getOrderDisplayPhoneSecondary, getOrderDisplayPhoneSource, getOrderDisplayPhoneSecondarySource } from "~/shared/utils/order-helpers";
import { formatDateWithYear, formatTimeRequired, formatWindowFilterDisplay, orDoubleDash, stripCountryCode } from "~/shared/utils/formatting";
import { InlineFilterDropdown } from "~/shared/components/InlineFilterDropdown";
import { AutocallOrderEditModal, type AutocallOrderEditUpdates } from "~/features/orders/components/AutocallOrderEditModal";
import { BulkWindowEditPanel, BULK_WINDOW_MIN_ORDERS } from "~/features/orders/components/BulkWindowEditPanel";
import { WindowPresetSelect } from "~/features/orders/components/WindowPresetSelect";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { formatWindowRange, type DeliveryWindowPreset, type WindowMinutes } from "~/shared/utils/delivery-window";
import { GlobalSearchBar } from "~/shared/components/GlobalSearchBar";
import type { FiltersContextual } from "~/features/orders/types-interfaces";

type DateFilterMode = 'today' | 'future' | 'all';

type ColumnServerFilters = { cities: string[]; dates: string[]; windows: string[] };

type Step1SelectProps = Readonly<{
  filteredOrders: Order[];
  loading?: boolean;
  dateFilter: DateFilterMode;
  todayCount: number;
  futureCount: number;
  totalCount: number;
  selected: Record<string, boolean>;
  selectedIds: string[];
  todayKey: string;
  allSelected: boolean;
  orderFilterValues: Record<string, string[]>;
  orderFilters: Record<string, Set<string>>;
  currentUserId: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
  // Server-side column filters (date, city, window) — trigger API call
  columnFilters: ColumnServerFilters;
  filtersContextual: FiltersContextual;
  onColumnFilterChange: (key: keyof ColumnServerFilters, selected: string[]) => void;
  // Client-side column filters (whiteGlove)
  onFilterSelectOnly: (column: string, selectedValues: string[]) => void;
  onFilterClear: (column: string) => void;
  onToggle: (id: string) => void;
  onSaveOrderEdit: (order: Order, updates: AutocallOrderEditUpdates) => Promise<void>;
  /** Grid Window cell: save a preset window for one order */
  onSaveOrderWindow: (order: Order, preset: DeliveryWindowPreset) => Promise<void>;
  /** Bulk panel: apply one window to the selected orders; resolves true when the panel can close */
  onApplyBulkWindow: (orders: Order[], window: WindowMinutes) => Promise<boolean>;
  /** Edit side panel about to open: lock the order; resolves false when it cannot be locked */
  onBeginOrderEdit: (order: Order) => Promise<boolean>;
  /** Edit side panel closed (saved or cancelled): release its lock */
  onEndOrderEdit: (order: Order) => void;
  onToggleAll: () => void;
  onSelectToday: () => void;
  onSelectFuture: () => void;
  onCreateAndStart: (flow: CallFlowDisplayName) => void;
  emptyAction?: React.ReactNode;
}>;


const whiteGloveLabel = (flag?: boolean) => flag ? 'Yes' : 'No';

/** Format RxStatus for display using canonical labels */
function formatRxStatus(status?: string | null): string {
  if (!status || !status.trim()) return '—';
  const normalized = status.toLowerCase().trim();
  switch (normalized) {
    case RxStatus.WAITING:
      return 'Waiting';
    case RxStatus.CALL_IN_PROGRESS:
      return 'Call InProgress';
    case RxStatus.COMPLETED:
      return 'Completed';
    default:
      return status.trim();
  }
}

/** Return CSS class(es) for the row based on selection, editing, white-glove, blocked, and call-in-progress state */
function rowClasses(order: Order, currentUserId: string, isSelected: boolean, isEditing: boolean): string {
  const blocked = order.isBlocked && !!order.isBlockedBy && order.isBlockedBy !== currentUserId;
  if (blocked) return 'blocked-by-other';
  if (order.rxStatus === RxStatus.CALL_IN_PROGRESS) return 'call-in-progress';
  const base = order.white_glove_service ? 'whiteglove-yes' : 'whiteglove-no';
  if (isEditing) return `${base} table-row--editing`;
  if (isSelected) return `${base} selected`;
  return base;
}

/** Extract a short display name from the "BAYSHORE\\USERNAME" format */
function formatBlockedByName(blockedBy?: string): string {
  if (!blockedBy) return 'another dispatcher';
  // "BAYSHORE\\JOHN.DOE" → "John.Doe"
  const parts = blockedBy.split('\\');
  const raw = parts.length > 1 ? parts[1] : parts[0];
  // Title-case each segment separated by dots
  return raw
    .split('.')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(' ');
}

export function Step1Select({
  filteredOrders,
  loading = false,
  dateFilter,
  todayCount,
  futureCount,
  totalCount,
  selected,
  selectedIds,
  todayKey,
  allSelected,
  orderFilterValues,
  orderFilters,
  currentUserId,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  columnFilters,
  filtersContextual,
  onColumnFilterChange,
  onFilterSelectOnly,
  onFilterClear,
  onToggle,
  onSaveOrderEdit,
  onSaveOrderWindow,
  onApplyBulkWindow,
  onBeginOrderEdit,
  onEndOrderEdit,
  onToggleAll,
  onSelectToday,
  onSelectFuture,
  onCreateAndStart,
  emptyAction,
}: Step1SelectProps) {
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [startCallDropdownOpen, setStartCallDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [editingRowOffset, setEditingRowOffset] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);
  const [lockingEditId, setLockingEditId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [savingWindowIds, setSavingWindowIds] = useState<ReadonlySet<string>>(new Set());
  const { presets } = useDeliveryWindows();
  const visibleOrderCount = filteredOrders.length;

  /** Greyed-out (blocked by other) orders shown at the end of the list */
  const sortedOrders = useMemo(() => {
    return [...filteredOrders].sort((a, b) => {
      const aBlocked = a.isBlocked && !!a.isBlockedBy && a.isBlockedBy !== currentUserId;
      const bBlocked = b.isBlocked && !!b.isBlockedBy && b.isBlockedBy !== currentUserId;
      if (aBlocked === bBlocked) return 0;
      return aBlocked ? 1 : -1;
    });
  }, [filteredOrders, currentUserId]);
  const isEmpty = sortedOrders.length === 0;

  // Bulk window edit works on the visible selected orders (the same set Start Calling uses)
  const selectedOrders = useMemo(() => filteredOrders.filter(o => selected[o.id]), [filteredOrders, selected]);
  const bulkEnabled = selectedOrders.length >= BULK_WINDOW_MIN_ORDERS;
  const sidePanelOpen = !!editingOrder || bulkOpen;

  // Nothing left to edit (e.g. selection cleared or locks lost): close the bulk panel
  useEffect(() => {
    if (bulkOpen && !savingBulk && selectedOrders.length === 0) setBulkOpen(false);
  }, [bulkOpen, savingBulk, selectedOrders.length]);

  // Edit side panel: the order is locked while the panel is open and released when it closes
  function closeEditPanel() {
    if (editingOrder) onEndOrderEdit(editingOrder);
    setEditingOrder(null);
  }

  async function openEditPanel(order: Order) {
    // Ignore while a lock request is in flight, or when this order is already open
    if (lockingEditId !== null || editingOrder?.id === order.id) return;
    closeEditPanel();
    setBulkOpen(false);
    setLockingEditId(order.id);
    try {
      if (await onBeginOrderEdit(order)) setEditingOrder(order);
    } finally {
      setLockingEditId(null);
    }
  }

  async function saveRowWindow(order: Order, preset: DeliveryWindowPreset) {
    setSavingWindowIds(prev => new Set(prev).add(order.id));
    try {
      await onSaveOrderWindow(order, preset);
    } finally {
      setSavingWindowIds(prev => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  }

  async function applyBulkWindow(window: WindowMinutes) {
    setSavingBulk(true);
    try {
      if (await onApplyBulkWindow(selectedOrders, window)) setBulkOpen(false);
    } finally {
      setSavingBulk(false);
    }
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setStartCallDropdownOpen(false);
      }
    }
    if (startCallDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [startCallDropdownOpen]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedIds.length > 0 && selectedIds.length < visibleOrderCount;
    }
  }, [selectedIds, visibleOrderCount]);

  // Window filter values from the API are a named label for standard windows ("Morning (9am - 1pm)") or a
  // range key for unnamed ones; show both as a time range. Display only: the selected values sent back are unchanged.
  const formatWindowFilterOption = (value: string): string => {
    const standard = presets.find(p => p.group === 'standard' && p.label === value);
    return standard ? formatWindowRange(standard) : formatWindowFilterDisplay(value);
  };

  // Server-side column filter renderers (date, city, window) — trigger API call
  const serverFilterMap: Record<string, { key: keyof ColumnServerFilters; options: string[]; searchable?: boolean; formatOption?: (value: string) => string }> = {
    date: { key: "dates", options: filtersContextual.dates },
    city: { key: "cities", options: filtersContextual.cities, searchable: true },
    window: { key: "windows", options: filtersContextual.windows, formatOption: formatWindowFilterOption },
  };

  // Client-side column filter: compute selected from deselection-based state
  const whiteGloveOptions = orderFilterValues["whiteGlove"] ?? [];
  const whiteGloveDeselected = orderFilters["whiteGlove"];
  const whiteGloveSelected = whiteGloveDeselected && whiteGloveDeselected.size > 0
    ? whiteGloveOptions.filter(o => !whiteGloveDeselected.has(o))
    : [];

  const renderOrderFilterHeader = (column: string, label: string) => {
    const serverCfg = serverFilterMap[column];
    if (serverCfg) {
      return (
        <InlineFilterDropdown
          label={label}
          options={serverCfg.options}
          selected={columnFilters[serverCfg.key]}
          onChange={(sel) => onColumnFilterChange(serverCfg.key, sel)}
          searchable={serverCfg.searchable}
          formatOption={serverCfg.formatOption}
        />
      );
    }
    // Client-side: whiteGlove
    if (column === "whiteGlove") {
      return (
        <InlineFilterDropdown
          label={label}
          options={whiteGloveOptions}
          selected={whiteGloveSelected}
          onChange={(sel) => {
            if (sel.length === 0 || sel.length === whiteGloveOptions.length) {
              onFilterClear(column);
            } else {
              onFilterSelectOnly(column, sel);
            }
          }}
        />
      );
    }
    return <span style={{ fontWeight: 700 }}>{label}</span>;
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        width: '100%',
        minWidth: 0,
        flex: 1,
        minHeight: 0,
        alignItems: isEmpty && !sidePanelOpen ? 'flex-start' : 'stretch',
      }}
    >
      <section
        className="table-container table-container--scroll-inner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          position: 'relative',
        }}
        aria-label="Select orders"
      >
        {loading && filteredOrders.length > 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)',
              zIndex: 20,
            }}
          >
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
          </div>
        )}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--neutral-gray)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Left: Title + count */}
          <div style={{ fontWeight: 600, fontSize: '16px' }}>Orders</div>
          {selectedIds.length > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 24,
                height: 24,
                padding: '0 6px',
                borderRadius: 12,
                background: 'var(--primary-blue)',
                color: 'white',
                fontSize: 12,
                fontWeight: 600,
              }}
              aria-live="polite"
            >
              {selectedIds.length}
            </span>
          )}

          {/* Date filter buttons */}
          <button
            type="button"
            className={dateFilter === 'today' ? 'primary' : 'secondary'}
            onClick={onSelectToday}
            disabled={todayCount === 0}
            aria-pressed={dateFilter === 'today'}
          >
            Today
          </button>
          <button
            type="button"
            className={dateFilter === 'future' ? 'primary' : 'secondary'}
            onClick={onSelectFuture}
            disabled={futureCount === 0}
            aria-pressed={dateFilter === 'future'}
          >
            Future
          </button>

          {/* Edit Delivery Window (bulk): needs 2+ selected orders */}
          <button
            type="button"
            className="secondary"
            onClick={() => {
              closeEditPanel();
              setBulkOpen(true);
            }}
            disabled={!bulkEnabled || savingBulk}
            title={bulkEnabled ? 'Edit the delivery window for the selected orders' : `Select ${BULK_WINDOW_MIN_ORDERS} or more orders`}
          >
            Edit Delivery Window
          </button>

          {/* Start Calling */}
          {isDev ? (
            <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button
                type="button"
                className="primary"
                onClick={() => setStartCallDropdownOpen((v) => !v)}
                disabled={selectedIds.length === 0}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                aria-haspopup="true"
                aria-expanded={startCallDropdownOpen}
              >
                Start Calling
                <span style={{ fontSize: 10 }}>▾</span>
              </button>
              {startCallDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    margin: '4px 0 0 0',
                    padding: '4px 0',
                    background: 'var(--white)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    boxShadow: 'var(--shadow)',
                    minWidth: 140,
                    zIndex: 50,
                  }}
                >
                  {CALL_FLOW_OPTIONS.map((flow) => (
                    <button
                      key={flow}
                      type="button"
                      className="secondary"
                      style={{ width: '100%', justifyContent: 'flex-start', padding: '8px 16px', border: 'none', borderRadius: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 14, display: 'flex' }}
                      onClick={() => {
                        setStartCallDropdownOpen(false);
                        onCreateAndStart(flow);
                      }}
                    >
                      {flow}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="primary"
              onClick={() => onCreateAndStart(CALL_FLOW.FLOW_1)}
              disabled={selectedIds.length === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              Start Calling
            </button>
          )}

          <div style={{ flex: 1 }} />

          {/* Search bar */}
          <GlobalSearchBar
            value={searchQuery}
            onChange={onSearchChange}
            onSubmit={onSearchSubmit}
          />
        </div>

        <div
          className="table-scroll-wrapper"
          style={{ flex: isEmpty ? '0 0 auto' : 1, overflowY: isEmpty ? 'hidden' : 'auto', minHeight: 0, position: 'relative' }}
        >
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={onToggleAll}
                    aria-label="Select all orders"
                  />
                </th>
                <th>SM</th>
                <th>Patient</th>
                <th>Patient ID</th>
                <th>{renderOrderFilterHeader("date", "Date")}</th>
                <th>Time Required</th>
                <th>{renderOrderFilterHeader("window", "Window")}</th>
                <th>{renderOrderFilterHeader("city", "City")}</th>
                <th>Postal</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Alt Phone</th>
                <th>{renderOrderFilterHeader("whiteGlove", "White Glove")}</th>
                <th>ID Req</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isEmpty && (
                <tr>
                  <td
                    colSpan={17}
                    style={{ textAlign: 'center', padding: 0, color: 'var(--text-muted)', borderBottom: 'none' }}
                  >
                    <div
                      style={{
                        minHeight: 120,
                        padding: '16px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 600 }}>No orders</div>
                      {emptyAction}
                    </div>
                  </td>
                </tr>
              )}
              {sortedOrders.map(o => {
                const blockedByOther = !!o.isBlocked && !!o.isBlockedBy && o.isBlockedBy !== currentUserId;
                const callInProgress = o.rxStatus === RxStatus.CALL_IN_PROGRESS;

                const isEditing = editingOrder?.id === o.id;
                // Window cell is read-only while any of these hold
                const windowCellDisabled = [
                  blockedByOther,
                  callInProgress,
                  isEditing,
                  savingWindowIds.has(o.id),
                  presets.length === 0,
                ].includes(true);
                return (
                  <tr
                    key={o.id}
                    className={rowClasses(o, currentUserId, !!selected[o.id], isEditing)}
                    title={
                      blockedByOther ? `Locked by ${formatBlockedByName(o.isBlockedBy)}` :
                        callInProgress ? 'This order is currently being called' :
                          undefined
                    }
                  >
                    <td>
                      <input
                        aria-label={`Select ${o.sm_number}`}
                        type="checkbox"
                        checked={!!selected[o.id]}
                        disabled={blockedByOther || callInProgress}
                        onChange={() => onToggle(o.id)}
                      />
                    </td>
                    <td>
                      <strong>{o.sm_number}</strong>
                      {blockedByOther && (
                        <span className="blocked-by-badge" style={{ marginLeft: 6 }} title={`Blocked by ${formatBlockedByName(o.isBlockedBy)}`}>
                          🔒 {formatBlockedByName(o.isBlockedBy)}
                        </span>
                      )}
                    </td>
                    <td>{orDoubleDash(o.patient_name)}</td>
                    <td>{orDoubleDash(o.patient_id)}</td>
                    <td>{orDoubleDash(formatDateWithYear(o.requested_delivery_date ?? ''))}</td>
                    <td>{orDoubleDash(formatTimeRequired(o.time_required))}</td>
                    <td style={{ fontWeight: 600 }}>
                      <WindowPresetSelect
                        variant="cell"
                        value={o.preferred_window ?? null}
                        presets={presets}
                        onSelect={(preset) => saveRowWindow(o, preset)}
                        disabled={windowCellDisabled}
                        ariaLabel={`Delivery window for ${o.sm_number}`}
                      />
                    </td>
                    <td>{orDoubleDash(getOrderCity(o))}</td>
                    <td>{orDoubleDash(o.postal_code)}</td>
                    <td>{orDoubleDash(o.address)}</td>
                    <td>
                      {(() => {
                        const num = stripCountryCode(getOrderDisplayPhone(o));
                        const source = getOrderDisplayPhoneSource(o);
                        const display = orDoubleDash(num);
                        if (!num || display === '--') return display;
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                            <span>{display}</span>
                            {source === 'raw' && (
                              <span title="Raw number" style={{ color: 'var(--error-red, #c00)', fontSize: 12 }} aria-label="Raw number">✗</span>
                            )}
                            {source === 'ai_inferred' && (
                              <span title="AI inferred number" style={{ fontSize: 12 }} aria-label="AI inferred">🤖</span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const num = stripCountryCode(getOrderDisplayPhoneSecondary(o));
                        const source = getOrderDisplayPhoneSecondarySource(o);
                        const display = orDoubleDash(num);
                        if (!num || display === '--') return display;
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                            <span>{display}</span>
                            {source === 'raw' && (
                              <span title="Raw number" style={{ color: 'var(--error-red, #c00)', fontSize: 12 }} aria-label="Raw number">✗</span>
                            )}
                            {source === 'ai_inferred' && (
                              <span title="AI inferred number" style={{ fontSize: 12 }} aria-label="AI inferred">🤖</span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td>{whiteGloveLabel(o.white_glove_service)}</td>
                    <td>{o.id_required ? 'Yes' : 'No'}</td>
                    <td><span className="badge info">{formatRxStatus(o.rxStatus)}</span></td>
                    <td>
                      <button
                        type="button"
                        className="secondary"
                        onClick={(e) => {
                          const row = (e.currentTarget as HTMLElement).closest("tr") as HTMLElement | null;
                          if (row && row.parentElement) {
                            const rowRect = row.getBoundingClientRect();
                            const parentRect = (row.closest(".table-container") as HTMLElement | null)?.getBoundingClientRect();
                            setEditingRowOffset(parentRect ? rowRect.top - parentRect.top : 28);
                          }
                          openEditPanel(o);
                        }}
                        disabled={blockedByOther || lockingEditId === o.id}
                        title={blockedByOther ? "Order is locked by another dispatcher" : "Edit date, window, and time required"}
                        style={{ padding: '4px 8px', fontSize: 14 }}
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <div
        style={{
          width: sidePanelOpen ? '400px' : '0px',
          flexShrink: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Fill parent height so the table can own scrolling inside the fixed layout.
          height: '100%',
          minHeight: 0,
        }}
      >
        {editingOrder && (
          <div style={{ width: '400px' }}>
            <AutocallOrderEditModal
              key={editingOrder.id}
              order={editingOrder}
              saving={savingEdit}
              tailTop={editingRowOffset}
              onClose={() => !savingEdit && closeEditPanel()}
              onSave={async (updates) => {
                try {
                  setSavingEdit(true);
                  await onSaveOrderEdit(editingOrder, updates);
                  closeEditPanel();
                } finally {
                  setSavingEdit(false);
                }
              }}
            />
          </div>
        )}
        {bulkOpen && !editingOrder && (
          <div style={{ width: '400px' }}>
            <BulkWindowEditPanel
              orders={selectedOrders}
              saving={savingBulk}
              onClose={() => !savingBulk && setBulkOpen(false)}
              onApply={applyBulkWindow}
            />
          </div>
        )}
      </div>
    </div>
  );
}
